import { Type } from "@angular/core";
import { DpmStaticComponent } from "./dpm-static/dpm-static.component";
import { DemStaticComponent } from "./dem-static/dem-static.component";
import { SqlCacheStaticComponent } from "./sql-cache-static/sql-cache-static.component";
import { VisitPlannerStaticComponent } from "./visit-planner-static/visit-planner-static.component";
import { VISIT_PLANNER_DASHBOARD_KEY, VISIT_PLANNER_DASHBOARD_NM } from "src/app/services/dashboard.service";
import { MarketAccessMoonlakeComponent } from "./market-access-moonlake/market-access-moonlake.component";

// Dashboards that ship inside the bundle instead of coming back from
// getDashboardAccess(). Each entry is injected into the dashboard list as an
// ordinary card and rendered through the single ":module/static" route, which
// picks the component to mount from the `ds_key` it is given. Adding a new
// static dashboard is a one-line change here — no new route, no new module
// wiring.
//
// Everything static lives under dashboard/static/ and nothing static lives
// outside it. Layout of this folder, and the convention every new static
// dashboard follows:
//
//   dashboard/static/
//     static-dashboards.ts        <- this registry (the only entry point
//                                    dashboard/ imports from)
//     static-dashboard-entry.ts   <- the "opened from its card" flag, which the
//                                    screens themselves import (see its header
//                                    for why it is not in here)
//     static-dashboard-host/      <- the shared ":module/static" route target
//     <name>-static/              <- one folder per dashboard: component
//                                    (.ts/.html/.scss) plus any service or
//                                    helper only that dashboard uses
//
// So a new static dashboard means: add dashboard/static/<name>-static/, add one
// entry below, and declare the component in dashbaord.module.ts. Its help text
// still goes in assets/help_info/<name>-static.json, alongside the other two —
// assets stay in the assets tree so the build copies them.
//
// Anything genuinely shared between static dashboards belongs at this level
// (dpm-static.service.ts is shared by DPM and DEM today and should move up here
// if a third dashboard needs it); anything shared with the *dynamic* dashboards
// belongs in components/shared/ or components/widget/, not here.
export interface StaticDashboard {
  // 10-digit dashboard_key, minted as YYMMDDHHmm from the moment the dashboard
  // was added (e.g. 2026-07-29 14:30 -> "2607291430"). Mirrors the shape of the
  // API's date-stamped keys so a static dashboard is indistinguishable from a
  // real one, and stays 10 digits until 2100. Real keys are 14 digits
  // (YYYYMMDDHHmmss), so these can never collide with one.
  //
  // Mint the key ONCE and never change it: it travels in bookmarked URLs and
  // namespaces the saved filter state under `dpm-static-filter-state:<key>`.
  // It is deliberately a constant rather than generated at runtime — a fresh
  // value on every load would break both of those.
  key: string;
  // Card title in the dashboard list, also stored as navbar_page_nm.
  name: string;
  // Component the ":module/static" route mounts for this key.
  component: Type<any>;
}

export const STATIC_DASHBOARDS: StaticDashboard[] = [
  {
    key: "20260731014118", // 2026-07-29 14:30:18
    name: "DPM-Static",
    component: DpmStaticComponent,
  },
  {
    key: "20260803092119", // 2026-08-03 09:21:19
    name: "DEM-Static",
    component: DemStaticComponent,
  },
  {
    // Keyed like its two siblings above, i.e. as a real date-stamped
    // dashboard_key. This registry only says which component a key mounts — the
    // CARD comes from getDashboardAccess like every other dashboard's, so this
    // value has to be the dashboard_key of that row. Change it here if the row
    // is created with a different one, and nowhere else: it is read from the
    // ds_key query param and namespaces this screen's saved filter state and
    // approval marks (`sql-cache-static-filter-state:<key>`,
    // `sql-cache-static-approved:<key>`), so it must never change afterwards.
    key: "20260811121000", // 2026-08-11 12:10:00
    name: "AI Cache Review",
    component: SqlCacheStaticComponent,
  },
  {
    key: VISIT_PLANNER_DASHBOARD_KEY,
    name: VISIT_PLANNER_DASHBOARD_NM,
    component: VisitPlannerStaticComponent,
  },
  {
    // Same arrangement as AI Cache Review: the card comes from
    // getDashboardAccess, so this must equal that row's dashboard_key. It also
    // namespaces the saved state (`market-access-moonlake-filter-state:<key>`).
    key: "20261019013916", // 2026-10-19 13:39:16
    name: "Market Access - Moonlake",
    component: MarketAccessMoonlakeComponent,
  },
  // Next one slots in here.
];

// Registry lookup. Keys arrive from decrypted query params as strings, but
// compare loosely on the caller's side by normalising to string first.
export function findStaticDashboard(key: any): StaticDashboard | undefined {
  if (key === null || key === undefined || key === "") {
    return undefined;
  }
  const k = String(key);
  return STATIC_DASHBOARDS.find((d) => d.key === k);
}

export function isStaticDashboardKey(key: any): boolean {
  return !!findStaticDashboard(key);
}
